import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Shield, Activity, Building2, Clock, UserCheck, UserX, AlertCircle, ArrowRight, FileText, Settings, X, RotateCcw, BarChart3, Zap, Ticket, FileSignature, Bell, MessageSquare } from 'lucide-react';
import api, { adminQuotesAPI } from '../../services/api';
import AROverview from '../../components/admin/AROverview';
import AdminAlertsWidget from '../../components/admin/AdminAlertsWidget';
import DraggableSection from '../../components/admin/DraggableSection';
import { usePreferencesStore } from '../../context/preferencesStore';
import { useAuthStore } from '../../context/authStore';
import { useBranding } from '../../hooks/useBranding';
import { isPastDate } from '../../utils/dates';
import { formatMoney } from '../../utils/format';

const roleBadges = {
  admin: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  technician: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
  company_admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  user: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
};

const actionLabels = {
  'register': 'User Registered',
  'login': 'User Login',
  'logout': 'User Logout',
  'role_changed': 'Role Changed',
  'user_deactivated': 'User Deactivated',
  'session_revoked': 'Session Revoked',
  'all_sessions_revoked': 'All Sessions Revoked',
  'password_reset': 'Password Reset',
  'password_change': 'Password Changed',
};

export default function AdminDashboardPage() {
  const { portalName } = useBranding();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [activeAgreements, setActiveAgreements] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(true);
  const [quoteQuestions, setQuoteQuestions] = useState({ unreadCount: 0, quotesWithQuestions: 0, recentQuestions: [] });
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Layout customization state
  const [editMode, setEditMode] = useState(false);
  const [tempSections, setTempSections] = useState(null);
  const {
    adminDashboardSections,
    setAdminDashboardSectionOrder,
    getAdminDashboardDefaults
  } = usePreferencesStore();

  // Get user's admin permissions early for conditional fetching
  const { user } = useAuthStore();
  const adminPerms = user?.permissions?.admin || [];

  useEffect(() => {
    fetchStats();
    // Only fetch tickets if user has tickets permission
    if (adminPerms.includes('tickets')) {
      fetchRecentTickets();
    }
    // Only fetch invoices if user has invoices permission
    if (adminPerms.includes('invoices')) {
      fetchOpenInvoices();
    }
    // Only fetch agreements if user has agreements permission
    if (adminPerms.includes('agreements')) {
      fetchActiveAgreements();
    }
    // Only fetch quote questions if user has quotes permission
    if (adminPerms.includes('quotes')) {
      fetchQuoteQuestions();
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data.stats);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentTickets = async () => {
    try {
      const response = await api.get('/admin/recent-tickets?limit=10');
      setRecentTickets(response.data.tickets || []);
    } catch (err) {
      console.error('Failed to load recent tickets:', err);
    } finally {
      setTicketsLoading(false);
    }
  };

  const fetchOpenInvoices = async () => {
    try {
      const response = await api.get('/admin/open-invoices?limit=10');
      setOpenInvoices(response.data.invoices || []);
    } catch (err) {
      console.error('Failed to load open invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchActiveAgreements = async () => {
    try {
      const response = await api.get('/admin/active-agreements?limit=10');
      setActiveAgreements(response.data.agreements || []);
    } catch (err) {
      console.error('Failed to load active agreements:', err);
    } finally {
      setAgreementsLoading(false);
    }
  };

  const fetchQuoteQuestions = async () => {
    try {
      const response = await adminQuotesAPI.getQuestionsSummary(5);
      setQuoteQuestions(response.data);
    } catch (err) {
      console.error('Failed to load quote questions:', err);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Section configuration with required permissions
  // adminPerm: required admin permission from role's permissions.admin array
  const SECTION_CONFIGS = {
    statsCards: {
      title: 'Statistics Overview',
      icon: BarChart3,
      // No permission required - basic dashboard stats
    },
    paymentAlerts: {
      title: 'Payment Alerts',
      icon: Bell,
      adminPerm: 'invoices', // Show for users with invoices/payments permission
    },
    quoteQuestions: {
      title: 'Quote Questions',
      icon: MessageSquare,
      adminPerm: 'quotes', // Show for users with quotes permission
    },
    recentTickets: {
      title: 'Recent Tickets',
      icon: Ticket,
      adminPerm: 'tickets', // Show for users with tickets permission
    },
    openInvoices: {
      title: 'Open Invoices',
      icon: FileText,
      adminPerm: 'invoices', // Show for users with invoices permission
    },
    activeAgreements: {
      title: 'Active Agreements',
      icon: FileSignature,
      adminPerm: 'agreements', // Show for users with agreements permission
    },
    usersByRole: {
      title: 'Users by Role',
      icon: Users,
      adminPerm: 'users', // Only show for users with users permission
    },
    recentActivity: {
      title: 'Recent Activity',
      icon: Clock,
      adminPerm: 'audit', // Only show for users with audit permission
    },
    accountsReceivable: {
      title: 'Accounts Receivable',
      icon: FileText,
      adminPerm: 'accounts_receivable', // Only show for finance/admin users
    },
    quickLinks: {
      title: 'Quick Links',
      icon: Zap,
      // No permission required - links are filtered separately
    },
  };

  // Check if user has permission for a section
  const hasPermission = (sectionId) => {
    const config = SECTION_CONFIGS[sectionId];
    if (!config) return false;
    // No permission required = always show
    if (!config.adminPerm) return true;
    // Check if user has the required permission
    return adminPerms.includes(config.adminPerm);
  };

  // Edit mode handlers
  const handleEnterEditMode = () => {
    setTempSections([...adminDashboardSections]);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setTempSections(null);
    setEditMode(false);
  };

  const handleSaveLayout = () => {
    setAdminDashboardSectionOrder(tempSections);
    setTempSections(null);
    setEditMode(false);
  };

  const handleResetToDefaults = () => {
    setTempSections([...getAdminDashboardDefaults()]);
  };

  const handleToggleSectionExpanded = (sectionId) => {
    setTempSections(prev =>
      prev.map(s => s.id === sectionId ? { ...s, defaultExpanded: !s.defaultExpanded } : s)
    );
  };

  const handleDrop = (draggedId, targetId) => {
    setTempSections(prev => {
      const newSections = [...prev];
      const draggedIndex = newSections.findIndex(s => s.id === draggedId);
      const targetIndex = newSections.findIndex(s => s.id === targetId);

      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const [draggedSection] = newSections.splice(draggedIndex, 1);
      newSections.splice(targetIndex, 0, draggedSection);

      return newSections;
    });
  };

  // Get current sections (temp during edit, saved otherwise)
  const currentSections = editMode ? tempSections : adminDashboardSections;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p>{error}</p>
      </div>
    );
  }

  // Render section content based on section ID
  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'statsCards':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Users */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Users</p>
                  <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                    {stats.totalUsers}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <span className="text-green-600 font-medium">+{stats.newUsersLast7Days}</span>
                <span className="text-neutral-500 ml-1">new this week</span>
              </div>
            </div>

            {/* Active Sessions */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Active Sessions</p>
                  <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                    {stats.activeSessions}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <span className="text-neutral-600 dark:text-neutral-400 font-medium">{stats.recentLogins}</span>
                <span className="text-neutral-500 ml-1">logins today</span>
              </div>
            </div>

            {/* Companies */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Companies</p>
                  <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                    {stats.totalCompanies}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <span className="text-neutral-600 dark:text-neutral-400 font-medium">{stats.externalUsers}</span>
                <span className="text-neutral-500 ml-1">customer users</span>
              </div>
            </div>

            {/* Internal Staff */}
            <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Internal Staff</p>
                  <p className="text-2xl font-semibold text-neutral-900 dark:text-white mt-1">
                    {stats.internalUsers}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <span className="text-neutral-500">{portalName} team members</span>
              </div>
            </div>
          </div>
        );

      case 'paymentAlerts':
        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-neutral-900 dark:text-white">Payment Alerts</h3>
              <Link
                to="/admin/client-payments"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                Manage payments
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <AdminAlertsWidget limit={5} />
          </div>
        );

      case 'quoteQuestions':
        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-neutral-900 dark:text-white">Quote Questions</h3>
                {quoteQuestions.unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                    {quoteQuestions.unreadCount} unread
                  </span>
                )}
              </div>
              <Link
                to="/admin/quotes"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                View all quotes
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {questionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : quoteQuestions.recentQuestions.length > 0 ? (
              <div className="space-y-3">
                {quoteQuestions.recentQuestions.map((question) => (
                  <Link
                    key={question.id}
                    to={`/quotes/${question.opportunity_id}?from=admin`}
                    className="block py-3 px-3 -mx-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors border-b border-neutral-100 dark:border-neutral-700 last:border-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                          Quote #{question.opportunity_id}
                        </p>
                        <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-1 line-clamp-2">
                          "{question.message}"
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          From: {question.author_name || 'Anonymous'} {question.author_email && `(${question.author_email})`}
                        </p>
                      </div>
                      <span className="text-xs text-neutral-400 whitespace-nowrap">
                        {formatDate(question.created_at)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-8">
                No unanswered questions
              </p>
            )}
            {quoteQuestions.quotesWithQuestions > 0 && (
              <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-700">
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {quoteQuestions.quotesWithQuestions} quote{quoteQuestions.quotesWithQuestions !== 1 ? 's' : ''} with unanswered questions
                </p>
              </div>
            )}
          </div>
        );

      case 'recentTickets':
        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-neutral-900 dark:text-white">Recent Tickets</h3>
              <Link
                to="/admin/tickets"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                View all
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {ticketsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : recentTickets.length > 0 ? (
              <div className="space-y-3">
                {recentTickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}?from=admin`}
                    className="block py-3 px-3 -mx-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors border-b border-neutral-100 dark:border-neutral-700 last:border-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                          #{ticket.id} - {ticket.summary}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          {ticket.company} • {ticket.board}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          ticket.priority?.toLowerCase().includes('high') || ticket.priority?.toLowerCase().includes('critical')
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                            : ticket.priority?.toLowerCase().includes('medium')
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300'
                            : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300'
                        }`}>
                          {ticket.priority || 'Normal'}
                        </span>
                        <span className="text-xs text-neutral-400">
                          {formatDate(ticket.dateEntered)}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-8">
                No open tickets found
              </p>
            )}
          </div>
        );

      case 'openInvoices':
        // Helper to check if invoice is overdue
        const isOverdue = (dueDate) => {
          if (!dueDate) return false;
          return isPastDate(dueDate);
        };

        // Helper to format currency
        const formatCurrency = (amount) => {
          return formatMoney(amount || 0);
        };

        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-neutral-900 dark:text-white">Open Invoices</h3>
              <Link
                to="/admin/invoices"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                View all
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {invoicesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : openInvoices.length > 0 ? (
              <div className="space-y-3">
                {openInvoices.map((invoice) => (
                  <Link
                    key={invoice.id}
                    to={`/admin/invoices/${invoice.id}`}
                    className="block py-3 px-3 -mx-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors border-b border-neutral-100 dark:border-neutral-700 last:border-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                          #{invoice.invoiceNumber}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          {invoice.company}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                          {formatCurrency(invoice.balance)}
                        </span>
                        <span className={`text-xs ${
                          isOverdue(invoice.dueDate)
                            ? 'text-red-600 dark:text-red-400 font-medium'
                            : 'text-neutral-400'
                        }`}>
                          {isOverdue(invoice.dueDate) ? 'Overdue: ' : 'Due: '}
                          {new Date(invoice.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-8">
                No open invoices found
              </p>
            )}
          </div>
        );

      case 'activeAgreements':
        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-neutral-900 dark:text-white">Active Agreements</h3>
              <Link
                to="/admin/agreements"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                View all
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {agreementsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activeAgreements.length > 0 ? (
              <div className="space-y-3">
                {activeAgreements.map((agreement) => (
                  <Link
                    key={agreement.id}
                    to={`/admin/agreements/${agreement.id}`}
                    className="block py-3 px-3 -mx-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors border-b border-neutral-100 dark:border-neutral-700 last:border-0"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                          {agreement.name}
                        </p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                          {agreement.company} • {agreement.type}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {agreement.billingAmount > 0 && (
                          <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                            {formatMoney(agreement.billingAmount)}
                            <span className="text-xs font-normal text-neutral-500">/{agreement.billingCycle || 'mo'}</span>
                          </span>
                        )}
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          agreement.noEndingDate
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                        }`}>
                          {agreement.noEndingDate ? 'Ongoing' : 'Fixed Term'}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-8">
                No active agreements found
              </p>
            )}
          </div>
        );

      case 'usersByRole':
        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
            <h3 className="font-medium text-neutral-900 dark:text-white mb-4">Users by Role</h3>
            <div className="space-y-3">
              {Object.entries(stats.usersByRole || {}).map(([role, count]) => (
                <div key={role} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${roleBadges[role] || roleBadges.user}`}>
                      {role.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-neutral-100 dark:bg-neutral-700 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full"
                        style={{ width: `${(count / stats.totalUsers) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-neutral-900 dark:text-white dark:text-white w-8 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Verified/Unverified */}
            <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-green-500" />
                  <span className="text-neutral-600 dark:text-neutral-400">Verified</span>
                </div>
                <span className="font-medium">{stats.verifiedUsers}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <div className="flex items-center gap-2">
                  <UserX className="w-4 h-4 text-yellow-500" />
                  <span className="text-neutral-600 dark:text-neutral-400">Unverified</span>
                </div>
                <span className="font-medium">{stats.unverifiedUsers}</span>
              </div>
            </div>
          </div>
        );

      case 'recentActivity':
        return (
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-neutral-900 dark:text-white">Recent Activity</h3>
              <Link
                to="/admin/audit"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                View all
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3">
              {stats.recentActivity?.length > 0 ? (
                stats.recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 py-2 border-b border-neutral-100 dark:border-neutral-700 last:border-0">
                    <div className="w-8 h-8 bg-neutral-100 dark:bg-neutral-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-neutral-900 dark:text-white">
                        {actionLabels[activity.action] || activity.action}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                        {activity.email || 'System'} - {formatDate(activity.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">No recent activity</p>
              )}
            </div>
          </div>
        );

      case 'accountsReceivable':
        return (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Accounts Receivable</h2>
              <Link
                to="/admin/invoices"
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                View all invoices
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <AROverview />
          </div>
        );

      case 'quickLinks':
        // Define quick links with their permission requirements
        const quickLinks = [
          { to: '/admin/tickets', perm: 'tickets', icon: Ticket, color: 'orange', title: 'Tickets', desc: 'View all tickets' },
          { to: '/admin/invoices', perm: 'invoices', icon: FileText, color: 'green', title: 'Invoices', desc: 'View all invoices' },
          { to: '/admin/companies', perm: 'companies', icon: Building2, color: 'blue', title: 'Companies', desc: 'View companies' },
          { to: '/admin/users', perm: 'users', icon: Users, color: 'purple', title: 'User Management', desc: 'Manage users and roles' },
          { to: '/admin/audit', perm: 'audit', icon: Shield, color: 'neutral', title: 'Audit Log', desc: 'View system activity' },
          { to: '/admin/sessions', perm: 'sessions', icon: Activity, color: 'cyan', title: 'Active Sessions', desc: 'Manage user sessions' },
        ].filter(link => adminPerms.includes(link.perm));

        const colorClasses = {
          blue: { bg: 'bg-blue-100', hover: 'group-hover:bg-blue-200', icon: 'text-blue-600' },
          purple: { bg: 'bg-purple-100', hover: 'group-hover:bg-purple-200', icon: 'text-purple-600' },
          green: { bg: 'bg-green-100', hover: 'group-hover:bg-green-200', icon: 'text-green-600' },
          orange: { bg: 'bg-orange-100', hover: 'group-hover:bg-orange-200', icon: 'text-orange-600' },
          neutral: { bg: 'bg-neutral-100', hover: 'group-hover:bg-neutral-200', icon: 'text-neutral-600' },
          cyan: { bg: 'bg-cyan-100', hover: 'group-hover:bg-cyan-200', icon: 'text-cyan-600' },
        };

        return quickLinks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickLinks.map(link => {
              const colors = colorClasses[link.color] || colorClasses.blue;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-5 hover:border-primary-300 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 ${colors.bg} rounded-lg flex items-center justify-center ${colors.hover} transition-colors`}>
                      <link.icon className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-neutral-900 dark:text-white">{link.title}</h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">{link.desc}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white dark:text-white">Admin Dashboard</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 dark:text-neutral-400 mt-1">
            System overview and statistics
          </p>
        </div>
        {!editMode && (
          <button
            onClick={handleEnterEditMode}
            className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            Customize Layout
          </button>
        )}
      </div>

      {/* Edit Mode Bar */}
      {editMode && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-primary-600" />
              <div>
                <h3 className="font-medium text-primary-900">Customize Dashboard Layout</h3>
                <p className="text-sm text-primary-700">Drag sections to reorder. Toggle checkboxes to set default expanded state.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleResetToDefaults}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 hover:bg-white rounded transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              <button
                onClick={handleCancelEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 hover:bg-white rounded transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSaveLayout}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Sections */}
      {currentSections?.map((section, index) => {
        const config = SECTION_CONFIGS[section.id];
        if (!config) return null;
        // Skip sections user doesn't have permission for
        if (!hasPermission(section.id)) return null;

        return (
          <DraggableSection
            key={section.id}
            section={{ ...section, index }}
            sectionConfig={config}
            editMode={editMode}
            onToggleExpanded={handleToggleSectionExpanded}
            onDrop={handleDrop}
          >
            {renderSectionContent(section.id)}
          </DraggableSection>
        );
      })}
    </div>
  );
}
